import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { colors, fonts } from '../components/UI'

// Set-password-once. Shown the first time an account signs in (typically via a
// magic link) before they have a password. Sets the password AND flips the
// account-level user_metadata.password_set flag so this step never shows again
// - on the tracker or anywhere else that shares this TIC identity. Styling
// mirrors ResetPasswordPage so the two read as one family.
function passwordProblem(pw) {
  if (!pw || pw.length < 8) return 'Password must be at least 8 characters'
  if (!/[A-Z]/.test(pw)) return 'Password must contain an uppercase letter'
  if (!/[a-z]/.test(pw)) return 'Password must contain a lowercase letter'
  if (!/[0-9]/.test(pw)) return 'Password must contain a number'
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Password must contain a special character (e.g. !@#$%^&*)'
  return null
}

export default function SetPasswordPage({ onDone }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    const problem = passwordProblem(password)
    if (problem) { setError(problem); return }
    if (password !== confirm) { setError('The two passwords do not match'); return }
    setLoading(true); setError('')
    const { error } = await supabase.auth.updateUser({ password, data: { password_set: true } })
    if (error) { setError(error.message); setLoading(false); return }
    setLoading(false)
    onDone && onDone()
  }

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', color: colors.text,
    fontSize: '16px', fontFamily: fonts.body,
    outline: 'none', boxSizing: 'border-box',
  }

  const reqs = [
    { label: '8+ chars', ok: password.length >= 8 },
    { label: 'Uppercase', ok: /[A-Z]/.test(password) },
    { label: 'Lowercase', ok: /[a-z]/.test(password) },
    { label: 'Number', ok: /[0-9]/.test(password) },
    { label: 'Special (!@#...)', ok: /[^A-Za-z0-9]/.test(password) },
  ]

  return (
    <div style={{
      minHeight: '100vh', minHeight: '100dvh',
      background: colors.navy,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: fonts.body, padding: '24px 20px',
      paddingTop: 'max(24px, env(safe-area-inset-top))',
    }}>
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{ width: '3px', height: '52px', background: colors.gold }} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontFamily: fonts.body, fontWeight: 300, fontSize: '14px', letterSpacing: '0.04em', color: `rgba(${colors.textRgb},0.4)`, marginBottom: '2px' }}>THE</div>
            <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '32px', letterSpacing: '0.04em', color: colors.text, lineHeight: 1 }}>INVESTING</div>
            <div style={{ fontFamily: fonts.heading, fontWeight: 300, fontSize: '16px', letterSpacing: '0.18em', color: colors.gold, lineHeight: 1.2 }}>COUPLE</div>
          </div>
        </div>
        <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '16px', letterSpacing: '0.22em', color: `rgba(${colors.textRgb},0.5)`, borderTop: `1px solid rgba(201,151,43,0.2)`, paddingTop: '8px', marginTop: '4px' }}>
          BUDGET TRACKER
        </div>
      </div>

      <div style={{ width: '100%', maxWidth: '400px', background: colors.card, border: '1px solid rgba(255,255,255,0.07)', borderTop: `3px solid ${colors.gold}`, borderRadius: '12px', padding: '28px 24px' }}>
        <div style={{ fontSize: '20px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '0.06em', marginBottom: '4px' }}>Set a password</div>
        <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, marginBottom: '24px', lineHeight: 1.6 }}>One quick step the first time you sign in. Set a password so you can always get back in, even if email is slow. You can still use sign-in links any time.</div>

        {error && <div style={{ background: 'rgba(224,82,82,0.1)', border: '1px solid rgba(224,82,82,0.25)', borderRadius: '10px', padding: '10px 14px', fontSize: '14px', color: '#f08080', fontFamily: fonts.body, marginBottom: '16px' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '14px', color: colors.muted, letterSpacing: '0.1em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '6px' }}>Choose a password</label>
            <input type="password" value={password} onChange={e => { setPassword(e.target.value); setError('') }} placeholder="At least 8 characters" autoComplete="new-password" required style={inputStyle} />
            {password.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {reqs.map(r => (
                  <span key={r.label} style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: r.ok ? 'rgba(61,184,122,0.12)' : 'rgba(255,255,255,0.05)', color: r.ok ? '#3db87a' : `rgba(${colors.textRgb},0.35)`, border: `1px solid ${r.ok ? 'rgba(61,184,122,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                    {r.ok ? '✓ ' : ''}{r.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', color: colors.muted, letterSpacing: '0.1em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '6px' }}>Confirm password</label>
            <input type="password" value={confirm} onChange={e => { setConfirm(e.target.value); setError('') }} placeholder="Repeat your password" autoComplete="new-password" required style={inputStyle} />
          </div>
          <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', background: colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: '0.1em', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Saving...' : 'Save and continue'}
          </button>
        </form>
      </div>
    </div>
  )
}
